import { Controller } from "@hotwired/stimulus"

// Drives the CourtTime app preview video.
//
// The <video> ships with preload="none" and no autoplay attribute, so the 3.3MB
// file isn't fetched until we call play() — the poster carries the hero until
// then. Playback starts when the video scrolls into view and pauses when it
// leaves, so it never burns battery off-screen.
export default class extends Controller {
  static targets = ["video", "playIcon", "pauseIcon", "toggle", "label"]

  connect() {
    // Once the viewer presses pause we stop autoplaying on scroll — their
    // choice sticks for the rest of the visit.
    this.paused = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!this.paused) this.play()
        } else {
          this.videoTarget.pause()
        }
      },
      { threshold: 0.25 }
    )
    this.observer.observe(this.videoTarget)

    this.videoTarget.addEventListener("play", this.syncIcons)
    this.videoTarget.addEventListener("pause", this.syncIcons)
    this.syncIcons()
  }

  disconnect() {
    this.observer?.disconnect()
    this.videoTarget.removeEventListener("play", this.syncIcons)
    this.videoTarget.removeEventListener("pause", this.syncIcons)
  }

  toggle() {
    if (this.videoTarget.paused) {
      this.paused = false
      this.play()
    } else {
      this.paused = true
      this.videoTarget.pause()
    }
  }

  play() {
    // Autoplay can still be refused (low power mode, browser policy) — leaving
    // the poster and the play button visible is a fine outcome.
    this.videoTarget.play().catch(() => {})
  }

  syncIcons = () => {
    const playing = !this.videoTarget.paused
    this.playIconTarget.classList.toggle("hidden", playing)
    this.pauseIconTarget.classList.toggle("hidden", !playing)
    // The visible label is the button's accessible name, so there is no aria-label to keep in sync.
    this.labelTarget.textContent = playing ? "Pause preview" : "Play preview"
  }
}
